import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Chip, CircularProgress, Snackbar, Alert,
  Tooltip, MenuItem, Select, FormControl, InputLabel, IconButton,
  LinearProgress, Collapse, Grid, Button
} from '@mui/material';
import {
  CloudUpload, CloudDone, AttachFile, Delete, CheckCircle,
  Visibility, CloudDownload, ExpandMore, ExpandLess,
  Warning, Business, AccountBalance, Refresh as RefreshIcon,
  ErrorOutline, InsertDriveFile, Event, Close
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import userService from '../../services/userService';
import { useDocumentCategories } from '../../constants/documentTypes';
import { getExpirationStatus } from '../../utils/documentExpiration';
import { MAX_BRANCH_DOCUMENT_UPLOAD_MB as MAX_MB } from '../../constants/uploadLimits';
// Fecha LOCAL, no UTC -- `toISOString()` adelanta el día hasta 6 horas antes de tiempo en
// huso horario de México (UTC-6), bloqueando el date-picker y marcando "vencido" antes de hora.
const TODAY = new Date().toLocaleDateString('en-CA');
const MATRIZ_VALUE = 'MATRIZ';
const isImage = (n) => /\.(jpe?g|png|gif|webp|bmp)$/i.test(n || '');
const isPDF = (n) => /\.pdf$/i.test(n || '');

const CAT_ICONS = {
  'Documentos Legales': '📋',
  'Comprobantes de Servicios': '🧾',
  'Dictámenes': '📝',
  'Planos y Fichas Técnicas': '📐',
  'Protección Civil': '🚨',
  'Programas y Bitácoras': '📅',
  'Personal y Normatividad': '👥',
};

function DocCard({ doc, file, expirationDate, persisted, uploading, onFileSelect, onRemove, onExpirationChange, onPreview, onDownload }) {
  const isReplacing = !!file && !!persisted;
  const persistedExp = persisted?.expirationDate?.substring(0, 10) || '';
  const { isExpired, isNearExpiry } = getExpirationStatus(persistedExp);

  let cardBg = 'white';
  let borderColor = '#e2e8f0';
  if (file) { cardBg = '#f0fff4'; borderColor = '#48bb78'; }
  else if (persisted) { cardBg = '#eff6ff'; borderColor = '#93c5fd'; }
  if (isExpired) borderColor = '#fc8181';
  if (isReplacing) { cardBg = '#fffbeb'; borderColor = '#f6ad55'; }

  return (
    <Paper elevation={0} sx={{
      border: `2px solid ${borderColor}`,
      borderRadius: 3,
      p: 2,
      bgcolor: cardBg,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
      transition: 'all 0.2s ease',
      minHeight: 160,
      '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.10)', transform: 'translateY(-2px)' },
    }}>
      {/* Título + badge en la misma fila */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Typography variant="body2" sx={{
          fontWeight: 600, color: '#1e293b', lineHeight: 1.4,
          fontSize: '0.8rem', flex: 1,
        }}>
          {doc.label}
        </Typography>
        <Box sx={{ flexShrink: 0 }}>
          {file ? (
            <Chip label={isReplacing ? 'Reemplazar' : 'Listo'} size="small"
              color={isReplacing ? 'warning' : 'success'}
              sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700 }} />
          ) : persisted ? (
            <Chip label={isExpired ? 'Vencido' : isNearExpiry ? 'Próx. vencer' : 'Subido'}
              size="small"
              color={isExpired ? 'error' : isNearExpiry ? 'warning' : 'primary'}
              icon={isExpired ? <Warning sx={{ fontSize: '11px !important' }} /> : <CheckCircle sx={{ fontSize: '11px !important' }} />}
              sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700 }} />
          ) : (
            <Chip label="Pendiente" size="small" variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20, color: '#94a3b8', borderColor: '#cbd5e1' }} />
          )}
        </Box>
      </Box>

      {/* Archivo seleccionado o nombre subido */}
      {(file || persisted) && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.8,
          bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 2, px: 1.2, py: 0.6,
        }}>
          <InsertDriveFile sx={{ fontSize: 16, color: file ? '#2e7d32' : '#1565c0', flexShrink: 0 }} />
          <Typography variant="caption" sx={{
            flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            color: file ? '#2e7d32' : '#1565c0', fontWeight: 600, fontSize: '0.72rem',
          }}>
            {file ? file.name : persisted.documentName}
          </Typography>
          {persisted && !file && (
            <Box sx={{ display: 'flex', gap: 0.2, flexShrink: 0 }}>
              {persisted.filePath && (isImage(persisted.documentName) || isPDF(persisted.documentName)) && (
                <Tooltip title="Vista previa">
                  <IconButton size="small" onClick={() => onPreview(persisted)} sx={{ p: 0.3, color: '#7c3aed' }}>
                    <Visibility sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              )}
              {persisted.filePath && (
                <Tooltip title="Descargar">
                  <IconButton size="small" onClick={() => onDownload(persisted)} sx={{ p: 0.3, color: '#2e7d32' }}>
                    <CloudDownload sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Vigencia */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
        <Event sx={{ fontSize: 15, color: isExpired ? '#c62828' : '#64748b' }} />
        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.72rem', flexShrink: 0 }}>
          Vigencia:
        </Typography>
        <input
          type="date"
          value={expirationDate || persistedExp}
          min={TODAY}
          onChange={e => onExpirationChange(doc.key, e.target.value)}
          disabled={uploading}
          style={{
            flex: 1, fontSize: '0.72rem',
            border: `1px solid ${isExpired ? '#fc8181' : '#cbd5e1'}`,
            borderRadius: 6, padding: '3px 6px',
            background: isExpired ? '#fff5f5' : isNearExpiry ? '#fffbeb' : 'white',
            color: isExpired ? '#c62828' : '#1e293b',
            outline: 'none', cursor: 'pointer',
            maxWidth: 130,
          }}
        />
      </Box>

      {/* Botón de acción */}
      <Box sx={{ mt: 'auto' }}>
        {file ? (
          <Button size="small" color="error" variant="outlined" fullWidth
            startIcon={<Delete sx={{ fontSize: 14 }} />}
            onClick={() => onRemove(doc.key)}
            disabled={uploading}
            sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.5, borderRadius: 2 }}>
            Quitar archivo
          </Button>
        ) : (
          <>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
              style={{ display: 'none' }}
              id={`doc-${doc.key}`}
              onChange={e => onFileSelect(doc.key, e)}
              disabled={uploading}
            />
            <label htmlFor={`doc-${doc.key}`} style={{ width: '100%' }}>
              <Button size="small" variant={persisted ? 'outlined' : 'contained'} fullWidth
                component="span"
                startIcon={persisted ? <AttachFile sx={{ fontSize: 14 }} /> : <CloudUpload sx={{ fontSize: 14 }} />}
                disabled={uploading}
                sx={{
                  textTransform: 'none', fontSize: '0.75rem', py: 0.6, borderRadius: 2,
                  ...(persisted ? {
                    borderColor: '#f59e0b', color: '#b45309',
                    '&:hover': { bgcolor: '#fffbeb', borderColor: '#f59e0b' },
                  } : {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    color: 'white',
                    '&:hover': { background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)' },
                  }),
                }}>
                {persisted ? 'Reemplazar' : 'Seleccionar'}
              </Button>
            </label>
          </>
        )}
      </Box>
    </Paper>
  );
}

function SectionBlock({ title, subtitle, icon: Icon, color, catColor, cats, files, expirationDates, uploadedMap, uploading, expandedSections, onToggle, onFileSelect, onRemove, onExpirationChange, onPreview, onDownload }) {
  const [sectionCollapsed, setSectionCollapsed] = useState(false);
  const allDocs = cats.flatMap(c => c.docs);
  const uploadedCount = allDocs.filter(d => uploadedMap[d.key]).length;
  const pendingCount = allDocs.filter(d => files[d.key]).length;

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', mb: 3 }}>
      {/* Header de sección — clic para colapsar */}
      <Box
        onClick={() => setSectionCollapsed(p => !p)}
        sx={{
          px: 3, py: 2.5, cursor: 'pointer',
          background: `linear-gradient(135deg, ${color[0]} 0%, ${color[1]} 100%)`,
          color: 'white',
          display: 'flex', flexDirection: 'column', gap: 1.5,
          userSelect: 'none',
          '&:hover': { filter: 'brightness(1.06)' },
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1, display: 'flex' }}>
              <Icon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{title}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>{subtitle}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
            onClick={e => e.stopPropagation()}>
            <Chip label={`${uploadedCount} / ${allDocs.length} subidos`}
              size="small"
              icon={<CloudDone sx={{ fontSize: '13px !important', color: 'white !important' }} />}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, fontSize: '0.75rem' }} />
            {pendingCount > 0 && (
              <Chip label={`${pendingCount} seleccionados`} size="small"
                sx={{ bgcolor: 'rgba(76,175,80,0.85)', color: 'white', fontWeight: 700, fontSize: '0.75rem' }} />
            )}
            <IconButton size="small" sx={{ color: 'white', ml: 0.5 }}
              onClick={() => setSectionCollapsed(p => !p)}>
              {sectionCollapsed ? <ExpandMore /> : <ExpandLess />}
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Collapse in={!sectionCollapsed}>
        {/* Progreso */}
        <LinearProgress
          variant="determinate"
          value={allDocs.length > 0 ? (uploadedCount / allDocs.length) * 100 : 0}
          sx={{ height: 4, bgcolor: 'rgba(0,0,0,0.06)' }}
          color="primary"
        />

        <Box sx={{ p: 3 }}>
        {cats.map(cat => {
          const catDocs = cat.docs;
          const catUploaded = catDocs.filter(d => uploadedMap[d.key]).length;
          const isCollapsed = !expandedSections[cat.categoria];

          return (
            <Box key={cat.categoria} sx={{ mb: 2 }}>
              {/* Encabezado de categoría */}
              <Box
                id={`cat-${cat.categoria}`}
                onClick={() => onToggle(cat.categoria)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 1, py: 1.2, cursor: 'pointer', mb: 1.5,
                  borderBottom: `2px solid ${catColor}`,
                  '&:hover': { bgcolor: `${catColor}0d` },
                  scrollMarginTop: 80,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '1rem' }}>{CAT_ICONS[cat.categoria] || '📄'}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.82rem', color: catColor }}>
                    {cat.categoria}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label={`${catUploaded}/${catDocs.length}`} size="small"
                    sx={{ bgcolor: `${catColor}18`, color: catColor, height: 18, fontSize: '0.68rem', fontWeight: 700 }} />
                  {isCollapsed ? <ExpandMore sx={{ fontSize: 18, color: catColor }} /> : <ExpandLess sx={{ fontSize: 18, color: catColor }} />}
                </Box>
              </Box>

              <Collapse in={!isCollapsed}>
                <Grid container spacing={4}>
                  {catDocs.map(doc => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={doc.key}>
                      <DocCard
                        doc={doc}
                        file={files[doc.key] || null}
                        expirationDate={expirationDates[doc.key] || ''}
                        persisted={uploadedMap[doc.key] || null}
                        uploading={uploading}
                        onFileSelect={onFileSelect}
                        onRemove={onRemove}
                        onExpirationChange={onExpirationChange}
                        onPreview={onPreview}
                        onDownload={onDownload}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </Box>
          );
        })}
      </Box>
      </Collapse>
    </Paper>
  );
}

// Reemplaza con el número real de WhatsApp de CONEM (formato internacional sin +)
const WA_NUMBER = '521XXXXXXXXXX';
const waUrl = (msg) => `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;

const WA_ICON = (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="white" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.116.554 4.103 1.523 5.828L.057 23.428a.5.5 0 0 0 .609.61l5.71-1.494A11.94 11.94 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.9a9.855 9.855 0 0 1-5.032-1.378l-.36-.214-3.733.977.998-3.645-.235-.374A9.855 9.855 0 0 1 2.1 12C2.1 6.533 6.533 2.1 12 2.1S21.9 6.533 21.9 12 17.467 21.9 12 21.9z"/>
  </svg>
);

const ADS_BASE = [
  {
    title: 'Inspección PIPC',
    body: 'Actualiza tu Programa Interno de Protección Civil con nuestros especialistas.',
    color: '#0d47a1',
    icon: '🛡️',
    waMessage: 'Hola CONEM, me interesa una inspección PIPC. ¿Pueden darme información?',
  },
  {
    title: 'Dictamen Eléctrico',
    body: 'Cumple con la NOM-001-SEDE. Agenda tu dictamen eléctrico hoy.',
    color: '#1565c0',
    icon: '⚡',
    waMessage: 'Hola CONEM, necesito agendar un Dictamen Eléctrico. ¿Cuál es el proceso?',
  },
  {
    title: 'Capacitación PC',
    body: 'Cursos certificados en protección civil para tu brigada de emergencias.',
    color: '#283593',
    icon: '📚',
    waMessage: 'Hola CONEM, me interesan los cursos de capacitación en protección civil.',
  },
  {
    title: 'Póliza Anual',
    body: 'Servicio integral de cumplimiento normativo con visitas incluidas.',
    color: '#01579b',
    icon: '📋',
    waMessage: 'Hola CONEM, quisiera información sobre la Póliza Anual de cumplimiento.',
  },
];

const getContextualAds = (side, hasExpiredDocs, expiredCount, hasNoDocs) => {
  if (hasExpiredDocs) {
    const urgentAd = {
      title: `${expiredCount} doc${expiredCount > 1 ? 's' : '.'} vencido${expiredCount > 1 ? 's' : ''}`,
      body: 'Una inspección puede ocurrir en cualquier momento. Renueva tus documentos hoy con nosotros.',
      color: '#b71c1c',
      icon: '⚠️',
      waMessage: `Hola CONEM, tengo ${expiredCount} documento(s) vencido(s) y necesito renovarlos urgente.`,
      urgent: true,
    };
    const supportAd = side === 'left' ? ADS_BASE[0] : ADS_BASE[1];
    return [urgentAd, supportAd];
  }
  if (hasNoDocs) {
    const startAd = {
      title: 'Asesoría gratuita',
      body: 'Comienza sin costo. Un especialista te guía en todo tu proceso de cumplimiento normativo.',
      color: '#1b5e20',
      icon: '🎯',
      waMessage: 'Hola CONEM, me interesa una asesoría gratuita para comenzar mi proceso de cumplimiento.',
    };
    const supportAd = side === 'left' ? ADS_BASE[2] : ADS_BASE[3];
    return [startAd, supportAd];
  }
  return side === 'left' ? [ADS_BASE[0], ADS_BASE[2]] : [ADS_BASE[1], ADS_BASE[3]];
};

function SideBanner({ hasExpiredDocs, expiredCount, hasNoDocs }) {
  // Solo existe una columna (izquierda) — getContextualAds('right', ...) nunca se
  // renderiza en ningún lado, así que antes se apilaban las 4 tarjetas aquí mismo.
  const ads = getContextualAds('left', hasExpiredDocs, expiredCount, hasNoDocs);

  const BannerCard = ({ ad }) => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        border: ad.urgent ? `2px solid ${ad.color}` : '1px solid #e2e8f0',
        boxShadow: ad.urgent
          ? `0 4px 16px ${ad.color}33`
          : '0 2px 12px rgba(13,71,161,0.08)',
      }}
    >
      {/* Header */}
      <Box sx={{
        background: `linear-gradient(145deg, ${ad.color} 0%, ${ad.color}bb 100%)`,
        py: 3, px: 2, textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
        <Box sx={{ position: 'absolute', bottom: -12, left: -12, width: 50, height: 50, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
        {ad.urgent && (
          <Box sx={{
            position: 'absolute', top: 8, right: 8,
            bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 1,
            px: 0.8, py: 0.2,
          }}>
            <Typography sx={{ color: 'white', fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.06em' }}>
              URGENTE
            </Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: '2.2rem', lineHeight: 1, position: 'relative' }}>{ad.icon}</Typography>
        <Typography sx={{ color: 'white', fontWeight: 800, display: 'block', mt: 1, fontSize: '0.85rem', lineHeight: 1.3, position: 'relative' }}>
          {ad.title}
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ px: 2, pt: 2, pb: 2, bgcolor: 'white' }}>
        <Typography sx={{ color: '#475569', fontSize: '0.74rem', lineHeight: 1.6, display: 'block', mb: 1.5 }}>
          {ad.body}
        </Typography>
        <Box sx={{
          bgcolor: `${ad.color}18`,
          color: ad.color, borderRadius: 2,
          textAlign: 'center', py: 0.7, px: 1,
          fontSize: '0.72rem', fontWeight: 700,
        }}>
          Más información →
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ width: 200, flexShrink: 0, display: { xs: 'none', xl: 'flex' }, flexDirection: 'column', pt: 0.5 }}>
      <Box sx={{ position: 'sticky', top: 80 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 2 }}>
          <Box sx={{ height: 1, flex: 1, bgcolor: '#e2e8f0' }} />
          <Typography sx={{ color: '#94a3b8', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            Servicios CONEM
          </Typography>
          <Box sx={{ height: 1, flex: 1, bgcolor: '#e2e8f0' }} />
        </Box>

        {ads.map((ad, i) => (
          <Box key={i} sx={{ mb: i < ads.length - 1 ? 2 : 0 }}>
            <BannerCard ad={ad} />
          </Box>
        ))}

        {/* Botón único de contacto WhatsApp */}
        <Box
          component="a"
          href={waUrl(`Hola CONEM, estoy en la plataforma de cumplimiento y me gustaría recibir asesoría.`)}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8,
            bgcolor: '#25D366', color: 'white', borderRadius: 3,
            py: 1.2, textDecoration: 'none', fontWeight: 800, fontSize: '0.76rem',
            boxShadow: '0 3px 10px rgba(37,211,102,0.4)',
            transition: 'filter 0.2s, transform 0.15s',
            '&:hover': { filter: 'brightness(1.08)', transform: 'scale(1.02)' },
          }}
        >
          {WA_ICON} Contáctanos
        </Box>

        <Box sx={{ textAlign: 'center', mt: 1.5 }}>
          <Typography sx={{ color: '#cbd5e1', fontSize: '0.62rem', letterSpacing: '0.05em' }}>
            conem.com.mx
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function SubirArchivos() {
  const [company, setCompany] = useState(null);
  const [branches, setBranches] = useState([]);
  // Un solo selector de ente (Matriz o una sucursal) en vez de dos secciones fijas —
  // deja explícito a quién corresponden los documentos que se están viendo/subiendo.
  const [selectedEntity, setSelectedEntity] = useState(MATRIZ_VALUE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [files, setFiles] = useState({});
  const [expirationDates, setExpirationDates] = useState({});
  const [uploadedSucursal, setUploadedSucursal] = useState({});
  const [uploadedMatriz, setUploadedMatriz] = useState({});
  const [loadingDocs, setLoadingDocs] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // El catálogo de documentos requeridos es el mismo para cualquier ente (matriz o
  // sucursal) -- cada uno es un inmueble distinto y necesita sus propios legales,
  // dictámenes, permisos, etc.
  const { categories: CATEGORIAS_DOCUMENTOS } = useDocumentCategories();

  useEffect(() => { init(); }, []);

  const init = async () => {
    try {
      setLoading(true);
      const me = await userService.getCurrentUser();
      if (!me?.company?.id) { setError('No se encontró la empresa asociada a su usuario.'); return; }
      setCompany(me.company);
      const res = await api.get(`/company-branches/company/${me.company.id}`);
      const active = (res.data || []).filter(b => b.isActive !== false);
      setBranches(active);
    } catch { setError('Error al cargar datos de la empresa.'); }
    finally { setLoading(false); }
  };

  const isMatriz = selectedEntity === MATRIZ_VALUE;
  const selectedBranchId = isMatriz ? null : Number(selectedEntity);
  const scope = isMatriz ? 'matriz' : 'sucursal';
  const entityLabel = isMatriz
    ? (company?.businessName || 'Matriz')
    : (branches.find(b => String(b.id) === selectedEntity)?.branchName || 'Sucursal');

  useEffect(() => { if (selectedBranchId) loadSucursalDocs(selectedBranchId); }, [selectedBranchId]);
  useEffect(() => { if (company?.id) loadMatrizDocs(company.id); }, [company]);

  const loadSucursalDocs = async (id) => {
    try {
      setLoadingDocs(true);
      const res = await api.get(`/branch-documents/branch/${id}`);
      const map = {}; (res.data || []).forEach(d => { map[d.documentType] = d; });
      setUploadedSucursal(prev => ({ ...prev, [id]: map }));
    } catch { } finally { setLoadingDocs(false); }
  };

  const loadMatrizDocs = async (companyId) => {
    try {
      const res = await api.get(`/branch-documents/company/${companyId}/matriz`);
      const map = {}; (res.data || []).forEach(d => { map[d.documentType] = d; });
      setUploadedMatriz(map);
    } catch { }
  };

  const reloadAll = useCallback(() => {
    if (selectedBranchId) loadSucursalDocs(selectedBranchId);
    if (company?.id) loadMatrizDocs(company.id);
  }, [selectedBranchId, company]);

  const handleEntityChange = (value) => {
    setSelectedEntity(value);
    // El ente que se está viendo cambió -- limpiar selección pendiente para que no
    // parezca que esos archivos son del nuevo ente.
    setFiles({});
    setExpirationDates({});
  };

  const handleFileSelect = (key, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      setSnackbar({ open: true, message: `"${file.name}" supera los ${MAX_MB}MB`, severity: 'warning' });
      e.target.value = ''; return;
    }
    setFiles(prev => ({ ...prev, [key]: file }));
    e.target.value = '';
  };

  const handleRemove = (key) => {
    setFiles(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const handleExpirationChange = (key, value) => {
    setExpirationDates(prev => ({ ...prev, [key]: value }));
  };

  const toggleSection = (label) => setExpandedSections(prev => ({ ...prev, [label]: !prev[label] }));

  const handlePreview = async (doc) => {
    setPreviewDoc(doc); setPreviewUrl(null); setPreviewLoading(true);
    try {
      const res = await api.get('/dropbox/preview-file', { params: { path: doc.filePath }, responseType: 'blob' });
      setPreviewUrl(window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] })));
    } catch { } finally { setPreviewLoading(false); }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await api.get('/dropbox/download-file', { params: { path: doc.filePath }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = doc.documentName;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null); setPreviewUrl(null);
  };

  // El preview es un overlay manual (no un <Dialog> de MUI), así que Escape no
  // cierra solo — hay que escucharlo explícitamente mientras esté abierto.
  useEffect(() => {
    if (!previewDoc) return;
    const handleKeyDown = (e) => { if (e.key === 'Escape') closePreview(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewDoc, previewUrl]);

  const totalAttached = Object.keys(files).filter(k => files[k]).length;

  const handleUpload = async () => {
    const pending = Object.entries(files).filter(([, f]) => f);
    if (!pending.length) return;
    setUploading(true); setProgress(0); setUploadResult(null);

    const allDocsFlat = CATEGORIAS_DOCUMENTOS.flatMap(c => c.docs);
    const labelFor = (k) => allDocsFlat.find(d => d.key === k)?.label || k;

    let ok = 0, fail = 0; const errors = []; const succeededKeys = [];

    // Todo lo pendiente en `files` pertenece al ente actualmente seleccionado -- el
    // selector limpia la selección al cambiar de ente, así que no hace falta namespacing.
    if (isMatriz ? !company?.id : !selectedBranchId) {
      setUploading(false);
      setUploadResult({ success: false, message: 'Sin empresa/sucursal asignada.' });
      return;
    }

    for (let i = 0; i < pending.length; i++) {
      const [key, file] = pending[i];
      try {
        const fd = new FormData();
        fd.append('file', file);
        if (isMatriz) fd.append('companyId', company.id); else fd.append('branchId', selectedBranchId);
        fd.append('documentType', key);
        fd.append('scope', scope);
        const exp = expirationDates[key]; if (exp) fd.append('expirationDate', exp);
        await api.post('/branch-documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
        ok++;
        succeededKeys.push(key);
      } catch (e) {
        fail++;
        // El rechazo por tamaño de Spring (413, antes de llegar al controller) responde
        // {error: "..."} en vez de {message: "..."} -- sin el fallback a data?.error, ese caso
        // caía al mensaje genérico de axios ("Request failed with status code 413").
        errors.push({ label: labelFor(key), message: e.response?.data?.message || e.response?.data?.error || e.message });
      }
      setProgress(Math.round(((i + 1) / pending.length) * 100));
    }

    setUploading(false);

    // Solo se limpia la selección local de los que sí se subieron — los que fallaron
    // deben seguir mostrando su archivo seleccionado (para reintentar), no desaparecer.
    if (succeededKeys.length > 0) {
      setFiles(prev => {
        const next = { ...prev };
        succeededKeys.forEach(fk => { delete next[fk]; });
        return next;
      });
      setExpirationDates(prev => {
        const next = { ...prev };
        succeededKeys.forEach(fk => { delete next[fk]; });
        return next;
      });
    }

    if (fail === 0) {
      setUploadResult({ success: true, message: `${ok} documento(s) subido(s) exitosamente` });
      reloadAll();
    } else if (ok > 0) {
      setUploadResult({ success: false, message: `${ok} documento(s) subido(s), ${fail} no se pudieron subir:`, details: errors });
      reloadAll();
    } else {
      setUploadResult({ success: false, message: `${fail} documento(s) no se pudieron subir:`, details: errors });
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
      <CircularProgress />
    </Box>
  );
  if (error) return <Box p={3}><Alert severity="error">{error}</Alert></Box>;

  const uploadedMap = isMatriz ? uploadedMatriz : (uploadedSucursal[selectedBranchId] || {});
  const allDocs = CATEGORIAS_DOCUMENTOS.flatMap(c => c.docs);
  const totalUploaded = Object.keys(uploadedMap).length;

  const expiredDocs = Object.values(uploadedMap)
    .filter(doc => doc.expirationDate && doc.expirationDate.substring(0, 10) < TODAY);
  const hasExpiredDocs = expiredDocs.length > 0;
  const expiredCount = expiredDocs.length;
  const hasNoDocs = totalUploaded === 0;

  return (
    <Box sx={{ bgcolor: '#f1f5f9', minHeight: '100vh', py: 3 }}>
      <Box sx={{
        display: 'flex', alignItems: 'flex-start',
        maxWidth: '1680px', mx: 'auto', px: 3, gap: 3,
      }}>
        <SideBanner hasExpiredDocs={hasExpiredDocs} expiredCount={expiredCount} hasNoDocs={hasNoDocs} />
        <Box sx={{ flex: 1, minWidth: 0 }}>

        {/* ── Header principal ── */}
        <Paper elevation={0} sx={{
          p: 3, mb: 3, borderRadius: 3,
          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
          color: 'white',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1.5, display: 'flex' }}>
                <CloudUpload sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Subir Documentos</Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>{company?.businessName}</Typography>
              </Box>
            </Box>
            <Tooltip title="Recargar">
              <IconButton onClick={reloadAll} disabled={loadingDocs || uploading} sx={{ color: 'white' }}>
                {loadingDocs ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {/* ── Selector de ente: deja explícito a quién corresponden los documentos ── */}
        <Paper elevation={0} sx={{
          p: 2, mb: 1.5, borderRadius: 3, bgcolor: '#e8eeff', border: '1px solid #c7d3f5',
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Ente</InputLabel>
            <Select
              label="Ente"
              value={selectedEntity}
              onChange={(e) => handleEntityChange(e.target.value)}
              disabled={uploading}
            >
              <MenuItem value={MATRIZ_VALUE}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalance sx={{ fontSize: 18, color: '#0d47a1' }} /> Matriz — {company?.businessName}
                </Box>
              </MenuItem>
              {branches.map(b => (
                <MenuItem key={b.id} value={String(b.id)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business sx={{ fontSize: 18, color: '#1565c0' }} /> {b.branchName}{b.isMainBranch ? ' (Principal)' : ''}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            {loadingDocs && <CircularProgress size={16} sx={{ color: '#1565c0' }} />}
            <Chip
              label={`${totalUploaded} / ${allDocs.length} subidos`}
              size="small"
              color={totalUploaded > 0 ? 'success' : 'default'}
              variant="outlined"
              icon={<CloudDone sx={{ fontSize: '13px !important' }} />}
              sx={{ fontWeight: 700, fontSize: '0.72rem' }}
            />
          </Box>
        </Paper>

        <Alert
          severity="info"
          icon={<Warning />}
          sx={{
            mb: 3, borderRadius: 2, bgcolor: '#e3f2fd', borderLeft: '4px solid #2196f3',
            '& .MuiAlert-icon': { color: '#1565c0' },
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Estás viendo los documentos de <strong>{entityLabel}</strong>. Cada sucursal y la matriz tienen su
            propio expediente — cambia el ente arriba para revisar o subir los documentos de otro.
          </Typography>
        </Alert>

        <Snackbar open={snackbar.open} autoHideDuration={5000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* ── Resultado de subida ── */}
        {uploadResult && (
          <Alert
            severity={uploadResult.success ? 'success' : 'error'}
            icon={uploadResult.success ? <CheckCircle /> : <ErrorOutline />}
            onClose={() => setUploadResult(null)}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{uploadResult.message}</Typography>
            {uploadResult.details && uploadResult.details.length > 0 && (
              <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
                {uploadResult.details.map((d, i) => (
                  <Typography component="li" variant="caption" key={i} sx={{ display: 'list-item' }}>
                    <strong>{d.label}</strong>: {d.message}
                  </Typography>
                ))}
              </Box>
            )}
          </Alert>
        )}

        {/* ── Documentos del ente seleccionado ── */}
        <SectionBlock
          title={`Documentos de ${entityLabel}`}
          subtitle="Mismo catálogo de documentos requerido para cada ente"
          icon={isMatriz ? AccountBalance : Business}
          color={['#0d47a1', '#1565c0']}
          catColor="#1e88e5"
          cats={CATEGORIAS_DOCUMENTOS}
          files={files}
          expirationDates={expirationDates}
          uploadedMap={uploadedMap}
          uploading={uploading}
          expandedSections={expandedSections}
          onToggle={toggleSection}
          onFileSelect={handleFileSelect}
          onRemove={handleRemove}
          onExpirationChange={handleExpirationChange}
          onPreview={handlePreview}
          onDownload={handleDownload}
        />

        {/* ── Barra fija de subida ── */}
        {(totalAttached > 0 || uploading) && (
          <Paper elevation={4} sx={{
            position: 'sticky', bottom: 16, borderRadius: 3, px: 3, py: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
            bgcolor: 'white', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            zIndex: 100,
          }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                {totalAttached} archivo{totalAttached !== 1 ? 's' : ''} listo{totalAttached !== 1 ? 's' : ''} para subir
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PDF, JPG, PNG, DOCX, XLSX · Máx. {MAX_MB}MB por archivo
              </Typography>
            </Box>

            {uploading ? (
              <Box sx={{ flex: 1, maxWidth: 300 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Subiendo…</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#1565c0' }}>{progress}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
              </Box>
            ) : (
              <Button
                variant="contained"
                size="large"
                startIcon={<CloudUpload />}
                onClick={handleUpload}
                disabled={totalAttached === 0}
                sx={{
                  textTransform: 'none', fontWeight: 700, px: 4, py: 1.2, borderRadius: 2,
                  background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                  boxShadow: '0 4px 12px rgba(21,101,192,0.4)',
                  '&:hover': { background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)', transform: 'translateY(-1px)' },
                  transition: 'all 0.2s',
                }}
              >
                Subir {totalAttached} archivo{totalAttached !== 1 ? 's' : ''} a Dropbox
              </Button>
            )}
          </Paper>
        )}

        </Box>{/* fin columna central */}
      </Box>{/* fin flex 3 columnas */}

      {/* ── Preview overlay ── */}
      {previewDoc && (
        <Box
          onClick={closePreview}
          sx={{
            position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.85)',
            zIndex: 9999, display: 'flex', flexDirection: 'column',
          }}>
          <Box
            onClick={e => e.stopPropagation()}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 3, py: 1.5, bgcolor: '#1a237e', flexShrink: 0,
            }}>
            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
              {previewDoc.documentName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Descargar">
                <IconButton size="small" onClick={() => handleDownload(previewDoc)} sx={{ color: 'white' }}>
                  <CloudDownload />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cerrar (Esc)">
                <IconButton
                  onClick={closePreview}
                  sx={{
                    color: 'white', bgcolor: 'rgba(255,255,255,0.12)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                  }}
                >
                  <Close />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box
            onClick={e => e.stopPropagation()}
            sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, overflow: 'auto' }}>
            {previewLoading ? (
              <CircularProgress sx={{ color: 'white' }} />
            ) : previewUrl ? (
              isImage(previewDoc.documentName) ? (
                <img src={previewUrl} alt={previewDoc.documentName} style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }} />
              ) : isPDF(previewDoc.documentName) ? (
                <iframe src={previewUrl} title={previewDoc.documentName} style={{ width: '100%', height: '85vh', border: 'none', borderRadius: 8 }} />
              ) : null
            ) : (
              <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>No se pudo cargar la vista previa</Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
